import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import commonStyles, { DARKGREY_COLOR } from "../styles/commonStyles";

const BUBBLE_WIDTH = 180;
const BUBBLE_MARGIN = 10;
const ARROW_SIZE = 12;
const MAX_MEASURE_ATTEMPTS = 10;
const MEASURE_RETRY_DELAY = 150;

/**
 * Overlay showing several tooltips at the same time (the react-native-elements
 * Tooltip opens a modal each, and only one modal can be displayed at once).
 * Each step highlights an anchor (a copy of the element rendered over the
 * overlay) with a bubble below, above or on its left. Pressing an anchor closes
 * the guide and runs its action, pressing elsewhere closes the guide.
 *
 * steps: [{ key, anchorRef, renderAnchor, text, placement: "bottom" | "top" | "left", width, onPress }]
 */
const SelectionGuide = ({ visible, steps, onClose }) => {
	const containerRef = useRef();
	const [layouts, setLayouts] = useState({});
	const [container, setContainer] = useState(null);

	// Positions of the anchors relative to the overlay. Measured again a bit
	// later while the overlay or an anchor is not laid out yet (e.g. during the
	// closing animation of a modal screen).
	const measure = useCallback(
		(attempt = 0) => {
			const retry = () => {
				if (attempt < MAX_MEASURE_ATTEMPTS) {
					setTimeout(() => measure(attempt + 1), MEASURE_RETRY_DELAY);
				}
			};
			if (!containerRef.current) {
				return retry();
			}
			containerRef.current.measureInWindow((cx, cy, width, height) => {
				if (!width || !height) {
					return retry();
				}
				setContainer({ width, height });
				let retried = false;
				steps.forEach(({ key, anchorRef }) => {
					if (!anchorRef.current) {
						if (!retried) {
							retried = true;
							retry();
						}
						return;
					}
					anchorRef.current.measureInWindow((x, y, w, h) => {
						if (!w || !h) {
							if (!retried) {
								retried = true;
								retry();
							}
							return;
						}
						setLayouts(prev => ({
							...prev,
							[key]: { x: x - cx, y: y - cy, width: w, height: h }
						}));
					});
				});
			});
		},
		[steps]
	);

	// Measured again when the displayed steps change
	const stepsKey = steps.map(({ key }) => key).join();

	useEffect(() => {
		if (visible) {
			setLayouts({});
			// Wait for the overlay to be laid out
			const timeout = setTimeout(() => measure(), 50);
			return () => clearTimeout(timeout);
		}
	}, [visible, stepsKey]);

	if (!visible) {
		return null;
	}

	const { font, white } = commonStyles;

	return (
		<View
			ref={containerRef}
			style={[StyleSheet.absoluteFill, styles.container]}
			onLayout={() => measure()}
		>
			<Pressable
				style={[StyleSheet.absoluteFill, styles.dim]}
				onPress={onClose}
			/>
			{container &&
				steps.map(
					({
						key,
						renderAnchor,
						text,
						placement,
						width: stepWidth,
						onPress
					}) => {
						const layout = layouts[key];
						if (!layout) {
							return null;
						}
						// Two default bubbles fit side by side with a small gap, even on
						// small screens (e.g. iPhone SE)
						const width =
							stepWidth ??
							Math.min(BUBBLE_WIDTH, (container.width - 3 * BUBBLE_MARGIN) / 2);
						const centerX = layout.x + layout.width / 2;
						const centerY = layout.y + layout.height / 2;
						let bubbleStyle;
						let arrowStyle;
						if (placement === "left") {
							bubbleStyle = {
								right: container.width - layout.x + BUBBLE_MARGIN,
								top: Math.max(BUBBLE_MARGIN, centerY - 40)
							};
							arrowStyle = {
								right: -ARROW_SIZE / 2,
								top: Math.min(centerY - bubbleStyle.top, 80) - ARROW_SIZE / 2
							};
						} else {
							const left = Math.min(
								Math.max(BUBBLE_MARGIN, centerX - width / 2),
								container.width - width - BUBBLE_MARGIN
							);
							bubbleStyle =
								placement === "top"
									? {
											left,
											bottom: container.height - layout.y + BUBBLE_MARGIN
									  }
									: {
											left,
											top: layout.y + layout.height + BUBBLE_MARGIN
									  };
							arrowStyle = {
								[placement === "top" ? "bottom" : "top"]: -ARROW_SIZE / 2,
								left: centerX - left - ARROW_SIZE / 2
							};
						}
						return (
							<React.Fragment key={key}>
								<Pressable
									style={[
										styles.anchor,
										{
											left: layout.x,
											top: layout.y,
											width: layout.width,
											height: layout.height
										}
									]}
									onPress={() => {
										onClose();
										onPress?.();
									}}
								>
									{/* Copy of the anchor: the press is handled by the guide */}
									<View pointerEvents="none" style={styles.anchorCopy}>
										{renderAnchor()}
									</View>
								</Pressable>
								<View style={[styles.bubble, { width }, bubbleStyle]}>
									<View style={[styles.arrow, arrowStyle]} />
									<Text style={[font, white]}>{text}</Text>
								</View>
							</React.Fragment>
						);
					}
				)}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		zIndex: 100,
		elevation: 100
	},
	dim: {
		backgroundColor: "rgba(250, 250, 250, 0.7)"
	},
	anchor: {
		position: "absolute",
		alignItems: "center",
		justifyContent: "center"
	},
	anchorCopy: {
		width: "100%",
		alignItems: "center"
	},
	bubble: {
		position: "absolute",
		padding: 12,
		borderRadius: 10,
		backgroundColor: DARKGREY_COLOR
	},
	arrow: {
		position: "absolute",
		width: ARROW_SIZE,
		height: ARROW_SIZE,
		backgroundColor: DARKGREY_COLOR,
		transform: [{ rotate: "45deg" }]
	}
});

export default memo(SelectionGuide);
